import { EntreeDepuis } from '../pipeline/EntreesPipeline.js';
import { can, formatDate, sujet } from '@artosera/shared';
import { Badge, Button, DropdownMenu, Select, type DropdownMenuEntry } from '@artosera/ui';
import { ChevronDown, Search } from 'lucide-react';
import type { JSX } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router';
import { useContacts } from '../../api/contacts.js';
import {
  useDemande,
  useDemandes,
  useModifierDemande,
  useRattacherDemande,
  type EtatDemande,
  type FiltresDemandes,
} from '../../api/demandes.js';
import { useEquipe } from '../../api/equipe.js';
import { identiteCourante, useIdentite } from '../../auth/identite.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { estApiError } from '../../api/errors.js';
import { useOeuvres } from '../../api/oeuvres.js';
import type { CompteSession } from '../../api/types.js';
import contactsStyles from './ContactsPage.module.css';
import { Monogramme } from '../../composants/Monogramme.js';
import styles from './DemandesSegment.module.css';

export interface DemandesSegmentProps {
  galerieSlug: string;
  compte: CompteSession;
}

const ETATS: readonly EtatDemande[] = ['a_traiter', 'repondue', 'close'];

/** `#demande-<id>` ; un fragment mal encodé (`#demande-%`) est illisible, pas une exception. */
function lireCible(hash: string): { idCible: string | null; cibleIllisible: boolean } {
  if (!hash.startsWith('#demande-')) return { idCible: null, cibleIllisible: false };
  try {
    return { idCible: decodeURIComponent(hash.slice('#demande-'.length)), cibleIllisible: false };
  } catch {
    return { idCible: null, cibleIllisible: true };
  }
}

function toneDemande(etat: EtatDemande): 'reserved' | 'available' | 'withdrawn' {
  if (etat === 'a_traiter') return 'reserved';
  if (etat === 'repondue') return 'available';
  return 'withdrawn';
}

/**
 * Segment « Demandes » du carnet (3.3, écran 3, S3-c) : liste, filtre par
 * état et assignation, action « Répondue » / « Clore » / assigner, et
 * rattachement au carnet pour les demandes venues du site sans contact
 * reconnu.
 *
 * L’assignation propose les membres habilités à traiter une demande,
 * lus par GET …/equipe sous memberships:read. Les accès de support sont
 * exclus ; l’API vérifie elle-même l’appartenance à la galerie.
 */
export function DemandesSegment({ galerieSlug, compte }: DemandesSegmentProps): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'contacts', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';

  const [searchParams, setSearchParams] = useSearchParams();
  const { hash } = useLocation();
  const [recherche, setRecherche] = useState('');
  const etatBrut = searchParams.get('etat');
  const etat = ETATS.some((code) => code === etatBrut) ? etatBrut! : '';
  function setEtat(valeur: string): void {
    const suivants = new URLSearchParams(searchParams);
    if (valeur) suivants.set('etat', valeur);
    else suivants.delete('etat');
    setSearchParams(suivants);
  }

  const [assignation, setAssignation] = useState('');
  const [page, setPage] = useState(1);

  const filtres: FiltresDemandes = useMemo(
    () => ({
      ...(recherche ? { q: recherche } : {}),
      ...(etat ? { etat: [etat as EtatDemande] } : {}),
      ...(assignation ? { assignation: assignation as 'aucune' | 'toute' } : {}),
      tri: 'recent',
      page,
      parPage: 50,
    }),
    [recherche, etat, assignation, page],
  );

  const liste = useDemandes(galerieSlug, filtres);
  const oeuvres = useOeuvres(galerieSlug, { parPage: 200, inclureArchivees: true });
  const contacts = useContacts(galerieSlug, { parPage: 200, inclureArchives: true });
  const modifier = useModifierDemande(galerieSlug);
  const rattacher = useRattacherDemande(galerieSlug);
  const { galerie, compte: compteContexte, galerieSlug: slugContexte } = useShellContext();
  const identite = useIdentite();
  const contexteActuel =
    slugContexte === galerieSlug &&
    galerie.slug === galerieSlug &&
    compteContexte.id === compte.id &&
    identite?.compteId === compte.id &&
    identite.galerieSlug === galerieSlug;
  const peutLireEquipe = contexteActuel && galerieALaction(galerie, 'memberships:read');
  const peutAssigner = contexteActuel && galerieALaction(galerie, 'inquiries:write');
  const equipe = useEquipe(galerieSlug, { enabled: peutLireEquipe });
  // Une requête désactivée peut encore avoir un résultat en cache : ses noms
  // ne sont utilisables que sous les autorisations du contexte courant.
  const membres = useMemo(
    () => (peutLireEquipe ? (equipe.data?.membres ?? []) : []),
    [peutLireEquipe, equipe.data?.membres],
  );
  const collegues = useMemo(
    () =>
      membres.filter(
        (membre) =>
          !membre.support &&
          membre.compteId !== compte.id &&
          can(sujet(membre.role, membre.droits), 'inquiries:write'),
      ),
    [membres, compte.id],
  );
  const assignationCourante = useRef({ peutAssigner, collegues, compteId: compte.id, galerieSlug });
  useLayoutEffect(() => {
    assignationCourante.current = { peutAssigner, collegues, compteId: compte.id, galerieSlug };
  }, [peutAssigner, collegues, compte.id, galerieSlug]);

  function assigner(demandeId: string, assigneeA: string | null): void {
    const courant = assignationCourante.current;
    const identite = identiteCourante();
    if (
      !courant.peutAssigner ||
      courant.compteId !== compte.id ||
      courant.galerieSlug !== galerieSlug ||
      identite?.compteId !== compte.id ||
      identite.galerieSlug !== galerieSlug
    )
      return;
    if (
      assigneeA !== null &&
      assigneeA !== compte.id &&
      !courant.collegues.some((membre) => membre.compteId === assigneeA)
    )
      return;
    modifier.mutate({ id: demandeId, corps: { assigneeA } });
  }

  const demandes = useMemo(() => liste.data?.demandes ?? [], [liste.data?.demandes]);

  // Lien direct vers une demande (tableau de bord, `#demande-<id>`) : la liste
  // ne charge que les 50 premières selon les filtres. Si la demande ciblée n'y
  // figure pas, elle est chargée seule et affichée en tête, ou signalée
  // introuvable, plutôt que de laisser le lien sans effet.
  const { idCible, cibleIllisible } = lireCible(hash);
  const cibleHorsListe =
    idCible !== null && liste.isSuccess && !demandes.some((demande) => demande.id === idCible);
  const cible = useDemande(galerieSlug, cibleHorsListe ? idCible : null);

  // Le focus ne va sur la demande ciblée qu'une fois, quand elle apparaît :
  // une recherche saisie ensuite recharge la liste sans le reprendre. Le
  // fragment disparu, une nouvelle ouverture de la même demande le reprend.
  const cibleOuverte = useRef<string | null>(null);
  useEffect(() => {
    if (idCible === null) {
      cibleOuverte.current = null;
      return;
    }
    if (cibleOuverte.current === idCible) return;
    const ligne = document.getElementById(`demande-${idCible}`);
    if (!ligne) return;
    cibleOuverte.current = idCible;
    ligne.scrollIntoView({ block: 'center' });
    ligne.focus();
  }, [idCible, demandes, cible.data]);

  function afficherToutes(): void {
    setRecherche('');
    setAssignation('');
    setPage(1);
    setEtat('');
  }

  function titreOeuvre(id: string | null): string {
    if (!id) return '—';
    return oeuvres.data?.oeuvres.find((oeuvre) => oeuvre.id === id)?.titre ?? '—';
  }

  function nomContact(id: string | null, repli: string): string {
    if (!id) return repli;
    return contacts.data?.contacts.find((contact) => contact.id === id)?.nomAffiche ?? repli;
  }

  function nomAssignation(assigneeA: string | null, assigneeNom?: string | null): string {
    if (!assigneeA) return '—';
    if (assigneeA === compte.id) return compte.nom;
    // Le nom rendu par l'API vaut pour tout le monde ; la liste de l'équipe
    // (réservée à qui lit les accès) ne sert que de repli.
    return (
      assigneeNom ??
      membres.find((membre) => membre.compteId === assigneeA)?.nom ??
      t('regie:contacts.demandes.assigneeAutre')
    );
  }

  function entreesAssignation(demandeId: string, assigneeA: string | null): DropdownMenuEntry[] {
    return [
      {
        type: 'item',
        item: {
          key: 'moi',
          label: t('regie:contacts.demandes.massigner'),
          disabled: assigneeA === compte.id,
          onSelect: () => assigner(demandeId, compte.id),
        },
      },
      ...collegues.map((membre): DropdownMenuEntry => ({
        type: 'item',
        item: {
          key: `membre-${membre.compteId}`,
          label: t('regie:contacts.demandes.assignerA', { nom: membre.nom }),
          disabled: assigneeA === membre.compteId,
          onSelect: () => assigner(demandeId, membre.compteId),
        },
      })),
      {
        type: 'item',
        item: {
          key: 'aucun',
          label: t('regie:contacts.demandes.retirerAssignation'),
          disabled: assigneeA === null,
          onSelect: () => assigner(demandeId, null),
        },
      },
    ];
  }

  return (
    <div className={styles.segment}>
      <div className={styles.filtres} role="search" aria-label={t('contacts:demandes.titre')}>
        <div className={contactsStyles.champRecherche}>
          <Search size={16} strokeWidth={1.5} aria-hidden />
          <input
            type="search"
            className={contactsStyles.saisieRecherche}
            placeholder={t('regie:contacts.demandes.rechercheChamp')}
            aria-label={t('regie:contacts.demandes.rechercheChamp')}
            value={recherche}
            onChange={(event) => {
              setRecherche(event.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className={styles.filtreItem}>
          <Select
            aria-label={t('regie:contacts.demandes.filtreEtat')}
            value={etat}
            onValueChange={(valeur) => {
              setEtat(valeur);
              setPage(1);
            }}
            placeholder={t('regie:contacts.demandes.tousLesEtats')}
            options={[
              { value: '', label: t('regie:contacts.demandes.tousLesEtats') },
              ...ETATS.map((code) => ({ value: code, label: t(`contacts:demandes.${code}`) })),
            ]}
          />
        </div>

        <div className={styles.filtreItem}>
          <Select
            aria-label={t('regie:contacts.demandes.filtreAssignation')}
            value={assignation}
            onValueChange={(valeur) => {
              setAssignation(valeur);
              setPage(1);
            }}
            placeholder={t('regie:contacts.demandes.touteAssignation')}
            options={[
              { value: '', label: t('regie:contacts.demandes.touteAssignation') },
              { value: 'aucune', label: t('regie:contacts.demandes.nonAssignees') },
              { value: 'toute', label: t('regie:contacts.demandes.assignees') },
            ]}
          />
        </div>
      </div>

      {cibleIllisible || cibleHorsListe ? (
        cibleIllisible || cible.isError ? (
          <p className={styles.cibleIntrouvable} role="status">
            {t('regie:contacts.demandes.cibleIntrouvable')}
          </p>
        ) : cible.data ? (
          <section
            className={styles.cible}
            data-ui="demande-ciblee"
            id={`demande-${cible.data.demande.id}`}
            tabIndex={-1}
            aria-label={cible.data.demande.sujet?.trim() || cible.data.demande.nom}
          >
            <p className={styles.cibleNote}>{t('regie:contacts.demandes.cibleHorsListe')}</p>
            <div className={styles.etatDate}>
              <Badge tone={toneDemande(cible.data.demande.etat)}>
                {t(`contacts:demandes.${cible.data.demande.etat}`)}
              </Badge>
              <time dateTime={cible.data.demande.recueLe} className={styles.date}>
                {t('regie:contacts.demandes.colonneDate')}{' '}
                {formatDate(new Date(cible.data.demande.recueLe), langue, 'court')}
              </time>
            </div>
            <h3 className={styles.sujet}>
              {cible.data.demande.sujet?.trim() || cible.data.oeuvre?.titre || '—'}
            </h3>
            <p className={styles.cibleContact}>
              {cible.data.demande.nom || cible.data.demande.email || '—'}
            </p>
            {cible.data.demande.message ? (
              <p className={styles.message}>{cible.data.demande.message}</p>
            ) : null}
            <div>
              <Button variant="secondary" size="sm" onClick={afficherToutes}>
                {t('regie:contacts.demandes.afficherToutes')}
              </Button>
            </div>
          </section>
        ) : (
          <p className={styles.vide} role="status">
            {t('commun:chargement')}
          </p>
        )
      ) : null}

      <div className={styles.liste}>
        {liste.isLoading ? (
          <p className={styles.vide}>{t('commun:chargement')}</p>
        ) : liste.isError ? (
          <p className={styles.vide} role="alert">
            {estApiError(liste.error) ? liste.error.message : t('commun:erreurReseau')}
          </p>
        ) : demandes.length === 0 ? (
          <p className={styles.vide}>{t('regie:contacts.demandes.aucunResultat')}</p>
        ) : (
          <ul className={styles.fiches}>
            {demandes.map((demande) => {
              const titre = titreOeuvre(demande.oeuvreId);
              const sujet = demande.sujet?.trim();
              const titreConnu = titre !== '—';
              const nom = demande.contactId
                ? nomContact(demande.contactId, demande.nom || demande.email)
                : demande.nom || demande.email || '—';
              return (
                <li
                  key={demande.id}
                  className={styles.fiche}
                  data-etat={demande.etat}
                  id={`demande-${demande.id}`}
                  tabIndex={-1}
                >
                  <div className={styles.ficheEtat}>
                    <Badge tone={toneDemande(demande.etat)}>
                      {t(`contacts:demandes.${demande.etat}`)}
                    </Badge>
                  </div>

                  <div className={styles.ficheObjet}>
                    <span
                      className={styles.sujet}
                      data-oeuvre={demande.oeuvreId ? 'true' : 'false'}
                    >
                      {titreConnu ? titre : sujet || '—'}
                    </span>
                    {titreConnu && sujet && sujet !== titre ? <span>{sujet}</span> : null}
                    {demande.message ? (
                      <span className={styles.message}>{demande.message}</span>
                    ) : null}
                  </div>

                  <div className={styles.ficheContact}>
                    <Monogramme nom={demande.contactId ? nom : ''} taille={28} />
                    {demande.contactId ? (
                      <Link
                        className={styles.lienContact}
                        to={`/${galerieSlug}/contacts/${demande.contactId}`}
                      >
                        {nom}
                      </Link>
                    ) : (
                      <span className={styles.contactLibre}>{nom}</span>
                    )}
                  </div>

                  <dl className={styles.ficheMeta}>
                    <div>
                      <dt>{t('regie:contacts.demandes.colonneDate')}</dt>
                      <dd>{formatDate(new Date(demande.recueLe), langue, 'court')}</dd>
                    </div>
                    <div>
                      <dt>{t('regie:contacts.demandes.colonneAssignee')}</dt>
                      <dd>{nomAssignation(demande.assigneeA, demande.assigneeNom)}</dd>
                    </div>
                  </dl>

                  <div className={styles.actions}>
                    <EntreeDepuis
                      source="demande"
                      id={demande.id}
                      contactId={demande.contactId ?? undefined}
                    />
                    {demande.etat !== 'repondue' ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          modifier.mutate({ id: demande.id, corps: { etat: 'repondue' } })
                        }
                      >
                        {t('regie:contacts.demandes.marquerRepondue')}
                      </Button>
                    ) : null}
                    {demande.etat !== 'close' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          modifier.mutate({ id: demande.id, corps: { etat: 'close' } })
                        }
                      >
                        {t('regie:contacts.demandes.clore')}
                      </Button>
                    ) : null}
                    {peutAssigner ? (
                      <DropdownMenu
                        key={`${compte.id}-${galerieSlug}`}
                        entries={entreesAssignation(demande.id, demande.assigneeA)}
                        trigger={
                          <Button variant="ghost" size="sm">
                            {t('regie:contacts.demandes.assigner')}
                            <ChevronDown size={14} strokeWidth={1.5} aria-hidden />
                          </Button>
                        }
                      />
                    ) : null}
                    {!demande.contactId ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={rattacher.isPending}
                        onClick={() => rattacher.mutate({ id: demande.id })}
                      >
                        {t('regie:contacts.demandes.rattacherAuCarnet')}
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {liste.data && liste.data.pagination.pages > 1 ? (
          <div className={styles.pagination}>
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((valeur) => Math.max(1, valeur - 1))}
            >
              {t('regie:oeuvres.pagination.precedent')}
            </Button>
            <span>
              {t('regie:oeuvres.pagination.page', {
                page: liste.data.pagination.page,
                pages: liste.data.pagination.pages,
              })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= liste.data.pagination.pages}
              onClick={() =>
                setPage((valeur) => Math.min(liste.data!.pagination.pages, valeur + 1))
              }
            >
              {t('regie:oeuvres.pagination.suivant')}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
